import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, ViewChild } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { AccountSharedModule } from '@app/account/account-shared.module';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { LibraryElementsModule } from '@app/library/library-elements.module';
import { VerticalCardComponent } from '@app/library/panes/grid-card-universal/vertical/vertical-card.component';
import { LibraryPanesModule } from '@app/library/panes/library-panes.module';
import { TeacherBookingSurfaceBlockComponent } from '@app/teachers/teacher-card/teacher-booking-surface-block/teacher-booking-surface-block.component';
import { MobileModalModule } from '@core/components/mobile-modal/mobile-modal.module';
import { ModalModule } from '@core/components/modal/modal.module';
import { SpinnersModule } from '@core/components/spinners/spinners.module';

import { IconComponent } from '@shared/icon/icon.component';
import { User } from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiCoverModule } from 'onclass-ui';
import { ILibraryTitle } from '../../library-title/library-title.component';
import { AbstractPane } from '../models/abstract-pane';
import { UserMiniCardComponent } from './user-mini-card/user-mini-card.component';

@Component({
  selector: 'app-teacher-drag-scroll',
  standalone: true,
  imports: [
    LocaleSharedModule,
    CommonModule,
    SpinnersModule,
    LibraryElementsModule,

    RouterModule,
    ScrollingModule,
    LibraryPanesModule,
    ModalModule,
    MobileModalModule,
    OnUiCoverModule,
    AccountSharedModule,
    TeacherBookingSurfaceBlockComponent,
    VerticalCardComponent,
    UserMiniCardComponent,
    IconComponent,
  ],
  templateUrl: './teacher-drag-scroll.component.html',
  styleUrls: ['./teacher-drag-scroll.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeacherDragScrollComponent extends AbstractPane {
  @ViewChild('scroller', { read: CdkVirtualScrollViewport })
  scroller: CdkVirtualScrollViewport;

  public isDragScrollEnabled: boolean = this.deviceService.isDesktop();
  public itemSize = 416;
  public showModal = false;
  public openedCardIndex: number = null;
  public reachedPrev = false;
  public reachedNext = false;
  public item: User;

  protected router = inject(Router);
  public deviceService = inject(DeviceDetectorService);

  protected onContentLoaded(viaQuery?: boolean): void {
    this.isVisible = !!this.users?.length;
  }

  public get titleAllBottom(): Partial<ILibraryTitle> {
    return { ...this.blockTitle, all_bottom: true };
  }

  public trackUserByFn(index, item: User): string {
    return item.id;
  }

  moveLeft() {
    this.scroller.scrollToIndex(0, 'smooth');
  }

  moveRight() {
    const offset = this.scroller.measureScrollOffset('left');
    this.scroller.scrollToOffset(offset + 2 * this.itemSize, 'smooth');
  }

  public onOpenCard(i) {
    this.openedCardIndex = i;
    this.item = this.users[i];
    this.checkReached();
    this.showModal = !!this.item;
    // logger.log('ContentDragScrollComponent:onOpenCard', i, this.item, this.showModal);
  }

  public get getSubjectId() {
    return this.item.profile.teaching_subjects[0]?.subject_id;
  }

  public onCloseCard() {
    // logger.log('ContentDragScrollComponent:onCloseCard');
    this.openedCardIndex = null;
    this.item = null;
    this.showModal = false;
  }

  public onCloseModal(event) {
    if (event) {
      this.onCloseCard();
    }
  }

  public togglePrev(event?) {
    if (this.openedCardIndex - 1 >= 0) {
      this.onOpenCard(this.openedCardIndex - 1);
    }
    this.checkReached();
  }

  public toggleNext(event?) {
    if (this.openedCardIndex + 1 <= this.users.length - 1) {
      this.onOpenCard(this.openedCardIndex + 1);
    }
    this.checkReached();
  }

  public checkReached() {
    const reachedPrev = this.openedCardIndex <= 0;
    const reachedNext = this.openedCardIndex >= this.users.length - 1;

    if (!reachedPrev && !reachedNext) {
      this.reachedPrev = this.reachedNext = false;
    } else if (reachedPrev && reachedNext) {
      this.reachedPrev = this.reachedNext = true;
    } else if (reachedPrev) {
      this.reachedPrev = true;
    } else if (reachedNext) {
      this.reachedNext = true;
    }
  }
}
